import {
  Stack,
  Typography,
  OutlinedInput,
  InputAdornment,
  Box,
  useTheme,
  Card,
  Toolbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Select,
  MenuItem,
  IconButton,
  TableSortLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import React, { useCallback, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';


import { TDocument } from '@/types/document';
import { ExtractParams } from '@/types/params';

import { useRenameDocument } from '@/api/endpoints/document/useRenameDocument';
import { useUpdateCategoryOfDocument } from '@/api/endpoints/document/useUpdateCategoryOfDocument';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { useGetVaultDocuments } from '@/api/endpoints/vault/useGetVaultDocuments';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useDocumentStatus, DocumentStatusProvider } from '@/context/DocumentStatusProvider';
import { formatFullDate } from '@/utils/date';


import DocumentDeleteModal from '@/components/document/DocumentDeleteModal';
import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';
import DocumentViewModal from '@/components/document/DocumentViewModal';

const TABLE_HEAD = [
  { id: 'original_name', label: 'Document Name', sortable: true, width: '30%' },
  { id: 'category', label: 'Document Category', sortable: false, width: '30%' },
  { id: 'date', label: 'Date Added', sortable: true, width: '10%' },
];

interface DropZoneProps {
  onFileDrop: (file: File) => void;
  isUploading: boolean;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDrop, isUploading, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (isUploading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        onFileDrop(file);
      }
    },
    [onFileDrop, isUploading]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&::after': isDragging
          ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 10,
            pointerEvents: 'none',
          }
          : {},
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 11,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <UploadCloud01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
            Drop file to upload
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface DocumentListLayoutProps {
  documents: TDocument[];
  columns: GridColDef[];
  isLoading: boolean;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  documentUploadButtonRef: React.RefObject<DocumentUploadButtonAPI>;
  isFileUploading: boolean;
  onFileSelect: (file: File) => void;
  onUploadDocumentClick: () => void;
  isDocumentDeleteModalOpen: boolean;
  documentToDelete: TDocument | null;
  onDocumentDeleteModalClose: () => void;
  onDocumentDeleted: () => void;
}

// Add helper function to format document name
const formatDocumentName = (name: string, maxLength = 50) => {
  if (name.length <= maxLength) return name;

  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${name.slice(0, maxLength - 3)}...`;
  }

  const extension = name.slice(lastDotIndex);
  const nameWithoutExtension = name.slice(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // 3 for "..."

  return `${nameWithoutExtension.slice(0, maxNameLength)}...${extension}`;
};

interface DocumentNameEditorProps {
  initialValue: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

const DocumentNameEditor: React.FC<DocumentNameEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (inputRef.current && inputRef.current.value.trim()) {
        onSave(inputRef.current.value.trim());
      } else {
        onCancel();
      }
    } else if (event.key === 'Escape') {
      onCancel();
    }
    event.stopPropagation();
  };

  const handleBlur = () => {
    if (inputRef.current && inputRef.current.value.trim()) {
      onSave(inputRef.current.value.trim());
    } else {
      onCancel();
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <input
      ref={inputRef}
      defaultValue={initialValue}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={handleClick}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #1976d2',
        borderRadius: '4px',
        padding: '0 8px',
        fontSize: 'inherit',
        outline: 'none',
      }}
    />
  );
};

export const DocumentListLayout: React.FC<DocumentListLayoutProps> = () => {
  // const DocumentListNoRowsOverlay = React.useCallback(
  //   () => (
  //     <NoRowsOverlay
  //       entityName="Document"
  //       addEntityButtonIcon={<UploadCloud01Icon />}
  //       addEntityButtonText="Upload Document"
  //       isLoading={isFileUploading}
  //       onAddEntityClick={onUploadDocumentClick}
  //     />
  //   ),
  //   [onUploadDocumentClick, isFileUploading]
  // );

  const { session } = useAuth();
  const { id } = useParams<ExtractParams<PMPRoutes.buildingViewDocumentList>>();
  const theme = useTheme();

  useDocumentStatus();
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<TDocument | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof TDocument>('original_name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const documentUploadButtonRef = useRef<DocumentUploadButtonAPI>(null);

  const {
    data: documents = [],
    refetch,
    isLoading,
  } = useGetVaultDocuments(
    {
      building_id: Number(id),
    },
    !!session?.access_token
  );

  const { data: categories = [] } = useGetDocumentCategories();

  const updateCategoryOfDocumentMutation = useUpdateCategoryOfDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const renameDocumentMutation = useRenameDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const handleCategoryChange = (documentId: string, newCategory: string) => {
    updateCategoryOfDocumentMutation.mutate({
      categoryId: Number(newCategory),
      id: documentId,
    });
  };

  const handleRequestSort = (property: keyof TDocument) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(e.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // const onDeleteDocumentClick = useCallback((document: TDocument) => {
  //   setDocumentToDelete(document);
  //   setIsDocumentDeleteModalOpen(true);
  // }, []);

  const onDocumentDeleteModalClose = useCallback(() => {
    setIsDocumentDeleteModalOpen(false);
  }, []);

  const handleOnDocumentDeleted = useCallback(() => {
    setDocumentToDelete(null);
    setIsDocumentDeleteModalOpen(false);
  }, []);

  const onFileSelect = (file: File) => {
    setIsFileUploading(true);
    setTimeout(() => setIsFileUploading(false), 2000);
  };

  const handleStartEditing = useCallback((document: TDocument) => {
    setEditingDocumentId(document.id);
  }, []);

  const handleSaveDocumentName = (newName: string) => {
    if (editingDocumentId && newName) {
      renameDocumentMutation.mutate({
        id: editingDocumentId,
        name: newName,
      });
      setEditingDocumentId(null);
    }
  };

  const handleCancelEditing = () => {
    setEditingDocumentId(null);
  };

  const handlePreviewDocument = useCallback((document: TDocument) => {
    setPreviewDocumentId(document.id);
    setPreviewDocumentCategoryId(document.category_id ? Number(document.category_id) : null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocumentId(null);
  }, []);

  const handleDocumentNameClick = useCallback(
    (document: TDocument) => {
      if (clickTimerRef.current === null) {
        // First click
        clickTimerRef.current = setTimeout(() => {
          // Single click - open preview
          handlePreviewDocument(document);
          clickTimerRef.current = null;
        }, 200); // 200ms threshold for double click
      } else {
        // Second click - double click
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
        handleStartEditing(document);
      }
    },
    [handlePreviewDocument, handleStartEditing]
  );

  const handleDeleteClick = (document: TDocument) => {
    setDocumentToDelete(document);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete.id });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const filteredData = useMemo(() => {
    let data = documents ?? [];
    if (filterName) {
      data = data.filter(doc =>
        doc.original_name?.toLowerCase().includes(filterName.toLowerCase())
      );
    }
    return [...data].sort((a, b) => {
      const aVal = a[orderBy as keyof TDocument];
      const bVal = b[orderBy as keyof TDocument];
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [documents, filterName, order, orderBy]);

  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  return (
    <DocumentStatusProvider>
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative', pt: { sm: 7 } }}>
        <DocumentUploadButton
          ref={documentUploadButtonRef}
          isFileUploading={isFileUploading}
          onFileSelect={onFileSelect}
          sx={{
            position: { sm: 'absolute' },
            top: { sm: -2 },
            right: { sm: 0 },
          }}
        />

        <DropZone onFileDrop={onFileSelect} isUploading={isFileUploading}>
          <Card>
            <Toolbar
              sx={{
                height: 64,
                display: 'flex',
                justifyContent: 'space-between',
                p: theme.spacing(2),
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'common.white',
              }}
            >
              <OutlinedInput
                fullWidth
                value={filterName}
                onChange={handleFilterName}
                placeholder="Search documents..."
                startAdornment={
                  <InputAdornment position="start">
                    <SearchMdIcon sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                }
                sx={{ maxWidth: 320, bgcolor: 'common.white' }}
              />
            </Toolbar>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {TABLE_HEAD.map(head => (
                      <TableCell
                        key={head.id}
                        align="left"
                        sortDirection={orderBy === head.id ? order : false}
                        sx={{
                          width: head.width,
                          borderRight: 'none',
                          '&:last-child': {
                            borderRight: 'none',
                          },
                          bgcolor: theme.palette.secondary[200],
                          textTransform: 'none',
                          color: 'text.primary',
                          '& .MuiTableSortLabel-root': {
                            textTransform: 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.875rem', sm: 'inherit' },
                            fontWeight: 'bold',
                            color: 'text.primary',
                            '&:hover': {
                              color: 'text.primary',
                            },
                            '&.Mui-active': {
                              color: 'text.primary',
                              '&:hover': {
                                color: 'text.primary',
                              },
                            },
                          },
                        }}
                      >
                        {head.sortable ? (
                          <TableSortLabel
                            active={orderBy === head.id}
                            direction={orderBy === head.id ? order : 'asc'}
                            onClick={() => handleRequestSort(head.id as keyof TDocument)}
                          >
                            {head.label}
                          </TableSortLabel>
                        ) : (
                          <Typography
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: { xs: '0.875rem', sm: 'inherit' },
                              fontWeight: 'bold',
                              color: 'text.primary',
                              '&:hover': {
                                color: 'text.primary',
                              },
                            }}
                          >
                            {head.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && displayedData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length} align="center">
                        No documents found
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading &&
                    displayedData.map(doc => (
                      <TableRow
                        key={String(doc.id)}
                        sx={{
                          bgcolor: theme.palette.secondary.contrastText,
                          '&:hover': {
                            backgroundColor: `${theme.palette.secondary[200]} !important`,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                          },
                          '&:hover .delete-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        <TableCell>
                          {editingDocumentId === doc.id ? (
                            <DocumentNameEditor
                              initialValue={doc.original_name}
                              onSave={handleSaveDocumentName}
                              onCancel={handleCancelEditing}
                            />
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                              onClick={() => handleDocumentNameClick(doc)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  '&:hover': {
                                    color: 'primary.main',
                                  },
                                }}
                                title={doc.original_name}
                              >
                                {formatDocumentName(doc.original_name)}
                              </Typography>
                              <IconButton
                                size="small"
                                className="delete-icon"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteClick(doc);
                                }}
                                sx={{
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  p: 0.5,
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.lighter',
                                  },
                                }}
                              >
                                <Trash01Icon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={doc.category_id?.toString() || ''}
                            onChange={e => handleCategoryChange(doc.id, e.target.value)}
                            size="small"
                            sx={{
                              width: '400px',
                              height: 32,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.secondary[400],
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.secondary[600],
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.secondary[600],
                                borderWidth: '1px',
                              },
                              '& .MuiSelect-select': {
                                py: 1,
                                px: 1.5,
                                fontSize: '0.875rem',
                                lineHeight: 1.2,
                              },
                            }}
                          >
                            {categories.map(category => (
                              <MenuItem key={category.id} value={category.id.toString()}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>{formatFullDate(new Date(doc.created_at))}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'common.white',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  size="small"
                  sx={{ width: 80 }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {`${page * rowsPerPage + 1}-${Math.min(
                    (page + 1) * rowsPerPage,
                    filteredData.length
                  )} of ${filteredData.length}`}
                </Typography>
                <IconButton
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  size="small"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
                  size="small"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </Box>
          </Card>
        </DropZone>

        {/* <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
          <DropZone onFileDrop={onFileSelect} isUploading={isFileUploading}>
            <DataGrid
              rows={documents}
              columns={columns}
              density="comfortable"
              disableColumnFilter
              disableColumnMenu
              disableEval
              loading={isLoading}
              slots={{
                noRowsOverlay: DocumentListNoRowsOverlay,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'skeleton',
                  noRowsVariant: 'skeleton',
                },
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              disableRowSelectionOnClick
            />
          </DropZone>
        </Box> */}

        {isDocumentDeleteModalOpen && documentToDelete && (
          <DocumentDeleteModal
            document={documentToDelete}
            onClose={onDocumentDeleteModalClose}
            onDocumentDeleted={handleOnDocumentDeleted}
          />
        )}

        {previewDocumentId && (
          <DocumentViewModal
            id={previewDocumentId}
            categoryId={previewDocumentCategoryId ?? undefined}
            onClose={handleClosePreview}
          />
        )}

        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          PaperProps={{
            sx: {
              minWidth: { xs: '90%', sm: 400 },
              maxWidth: { xs: '90%', sm: 500 },
            },
          }}
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this document?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </DocumentStatusProvider>
  );
};
